import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MDBadge from "components/MDBadge";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import AddIcon from "@mui/icons-material/Add";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import AddForm from "../components/AddForm";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "./data/dataTableData";
import { RequestService } from "services/RequestService";
import { object } from "prop-types";
import MDInput from "components/MDInput";

const validatePassword = (password) => {
  const errors = [];

  // ASP.NET Identity Default Password Policy
  if (password.length < 6) {
    errors.push("يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل (A-Z)");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على حرف صغير واحد على الأقل (a-z)");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على رقم واحد على الأقل (0-9)");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على رمز خاص واحد على الأقل (!@#$%^&*...)");
  }

  return errors;
};

function UsersDataTables() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState({
    columns: [
      {
        Header: "#",
        accessor: "index",
        width: "5%",
        sortable: true,
        align: "center",
      },
      {
        Header: "الاسم",
        accessor: "displayName",
        width: "20%",
        sortable: true,
      },
      {
        Header: "البريد الإلكتروني",
        accessor: "email",
        width: "20%",
        sortable: true,
      },
      {
        Header: "نوع المستخدم",
        accessor: "userTypeName",
        width: "15%",
        sortable: true,
      },
      {
        Header: "الشركة",
        accessor: "companyName",
        width: "15%",
        sortable: true,
      },
      {
        Header: "الحالة",
        accessor: "isEnabled",
        width: "10%",
        sortable: false,
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        width: "15%",
        sortable: false,
        align: "center",
      },
    ],
    rows: [],
  });
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success"
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    userId: null
  });
  const [updateDialog, setUpdateDialog] = useState({
    open: false,
    userData: null
  });
  const [addDialog, setAddDialog] = useState(false);
  const [userTypes, setUserTypes] = useState({});
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState({});

  const [addForm, setAddForm] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    userType: "",
    companyId: null,
  });

  const [updateForm, setUpdateForm] = useState({
    displayName: "",
    email: "",
    userType: "",
    companyId: "",
  });

  const [openForcePasswordDialog, setOpenForcePasswordDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState([]);
  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });

  const handleCloseNotification = () => {
    setNotification({ ...notification, show: false });
  };

  const showNotification = (message, type = "success") => {
    setNotification({
      show: true,
      message,
      type
    });
  };

  const handleDeleteClick = (userId) => {
    setDeleteDialog({ open: true, userId });
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ open: false, userId: null });
  };

  const handleConfirmDelete = async () => {
    try {
      await RequestService.delete(`users/${deleteDialog.userId}`);
      showNotification("تم حذف المستخدم بنجاح");
      fetchUsers(); // Refresh the list
    } catch (error) {
      showNotification(error.response?.data?.message || "حدث خطأ أثناء حذف المستخدم", "error");
    } finally {
      handleCloseDialog();
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      setLoading(true);
      let js = {
        UserId: userId,
        Status: !currentStatus
      };
      await RequestService.put("Auth/update-user-status", js);
      showNotification("تم تحديث حالة المستخدم بنجاح", "success");
      fetchUsers(); // Refresh the table
    } catch (error) {
      console.error("Error toggling user status:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء تحديث حالة المستخدم", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClick = (userData) => {
    setUpdateForm({
      displayName: userData.displayName || "",
      email: userData.email || "",
      userType: userData.userType || "", // Use the numeric userType directly
      companyId: userData.companyId || "",
    });
    setUpdateDialog({ open: true, userData });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog({ open: false, userData: null });
    setUpdateForm({
      displayName: "",
      email: "",
      userType: "",
      companyId: "",
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...updateForm,
        id: updateDialog.userData.id,
        isEnabled: updateDialog.userData.isEnabled
      };

      await RequestService.put(`Auth/update-user`, payload);
      showNotification("تم تحديث بيانات المستخدم بنجاح", "success");
      handleCloseUpdateDialog();
      fetchUsers(); // Refresh the list
    } catch (error) {
      showNotification(error.response?.data?.message || "حدث خطأ أثناء تحديث بيانات المستخدم", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddClick = () => {
    setAddForm({
      displayName: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: "",
      companyId: null,
    });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setAddForm({
      displayName: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: "",
      companyId: null,
      userName: "",
    });
  };

  const handleAddSubmit = async () => {
    if (!addForm.displayName) {
      showNotification("اسم المستخدم مطلوب", "error");
      return;
    } else if (!addForm.email) {
      showNotification("البريد الالكتروني مطلوب", "error");
      return;
    } else if (!addForm.userType) {
      showNotification("نوع المستخدم مطلوب", "error");
      return;
    } else if (!addForm.password) {
      showNotification("كلمة المرور مطلوبة", "error");
      return;
    } else if (addForm.password !== addForm.confirmPassword) {
      showNotification("كلمة المرور وتأكيد كلمة المرور غير متطابقين", "error");
      return;
    } else if (addForm.userType == 3 && !addForm.companyId) {
      showNotification('الشركة مطلوبة', "error");
    } else {
      try {
        setLoading(true);
        const payload = {
          displayName: addForm.displayName,
          email: addForm.email,
          password: addForm.password,
          userType: addForm.userType,
          companyId: addForm.companyId
        };

        await RequestService.post(`Auth/register`, payload);
        showNotification("تم إضافة المستخدم بنجاح", "success");
        handleCloseAddDialog();
        fetchUsers();
      } catch (error) {
        showNotification(error.response.data.message, 'error')
        console.log(error.response.data.message)
      } finally {
        setLoading(false);
      }
    }
  };

  const transformUserData = (users) => {
    return users.map((user, index) => ({
      index: ((pagination.page - 1) * pagination.pageSize) + index + 1,
      id: user.id,
      displayName: user.displayName || "—",
      email: user.email || "—",
      userTypeName: user.userTypeName || "—",
      companyName: user.companyName || "—",
      userType: user.userType,
      isEnabled: (
        <MDBox display="flex" alignItems="center" justifyContent="flex-end">
          <Switch
            checked={user.isEnabled}
            onChange={() => handleToggleUserStatus(user.id, user.isEnabled)}
            color="success"
          />
          <MDBox ml={1}>
            <MDTypography variant="button" fontWeight="regular" color={user.isEnabled ? "success" : "error"}>
              {user.isEnabled ? "نشط" : "غير نشط"}
            </MDTypography>
          </MDBox>
        </MDBox>
      ),
      actions: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleUpdateClick(user)}
            startIcon={<Icon>edit</Icon>}
          >
            تعديل
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() => {
              setSelectedUserId(user.id);
              setOpenForcePasswordDialog(true);
            }}
            startIcon={<Icon>key</Icon>}
          >
            تغيير كلمة المرور
          </MDButton>

        </MDBox>
      ),
    }));
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const skip = (pagination.page - 1) * pagination.pageSize;
      const response = await RequestService.get(`Auth/get-all-users?searchTerm=${search}&skip=${skip}&take=${pagination.pageSize}`);

      if (response.data && !response.data.error) {
        setTableData({
          columns: tableData.columns,
          rows: transformUserData(response.data.data)
        });
        setPagination(prev => ({
          ...prev,
          totalItems: response.data.totalCount
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      showNotification("حدث خطأ أثناء جلب المستخدمين", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };

  const fetchUserTypes = async () => {
    try {
      const response = await RequestService.get("Auth/get-all-roles");
      if (response.data && !response.data.error) {
        setUserTypes(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };


  const fetchCompanies = async () => {
    try {
      const response = await RequestService.get("Companies");
      if (response.data && !response.data.error) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showNotification("حدث خطأ أثناء جلب الشركات", "error");
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await RequestService.get("Auth/get-all-roles");
      if (response.data && !response.data.error) {
        setRoles(response.data.data || {});
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      showNotification("حدث خطأ أثناء جلب الأدوار", "error");
    }
  };

  const handleForceChangePassword = async () => {
    const errors = validatePassword(newPassword);
    setPasswordErrors(errors);

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("كلمات المرور غير متطابقة");
      return;
    } else {
      setConfirmPasswordError("");
    }

    if (errors.length > 0) {
      return;
    }

    try {
      const response = await RequestService.post("Auth/Sa-force-change-password", {
        userId: selectedUserId,
        newPassword: newPassword
      });

      if (response.data && !response.data.error) {
        showNotification("تم تغيير كلمة المرور بنجاح", "success");
        setOpenForcePasswordDialog(false);
        setNewPassword("");
        setConfirmPassword("");
        setPasswordErrors([]);
        setConfirmPasswordError("");
        setSelectedUserId(null);
      } else {
        showNotification("حدث خطأ أثناء تغيير كلمة المرور", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showNotification("حدث خطأ أثناء تغيير كلمة المرور", "error");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserTypes();
      await fetchCompanies();
    };

    initializeData();
  }, []);

  useEffect(() => {
    fetchUsers();
    console.log(tableData)
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true); {
        setPagination(prev => ({ ...prev, page: 1 }));
        const timer = setTimeout(() => {
          fetchUsers();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [search]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة المستخدمين
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة المستخدمين وصلاحياتهم
                  </MDTypography>
                </MDBox>
                <MDBox width="14rem" ml="auto">
                  <MDInput
                    placeholder="بحث ..."
                    value={search}
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                    }}
                    sx={{ margin: "0 -10px" }}
                  />
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                >
                  إضافة مستخدم
                </MDButton>
              </MDBox>
              <MDBox p={3}>

                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                  sx={{
                    "& .MuiTableHead-root": {
                      "& .MuiTableCell-root": {
                        backgroundColor: "grey.200",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                        textAlign: "right",
                        "&:first-of-type": {
                          borderTopRightRadius: "0.5rem",
                        },
                        "&:last-of-type": {
                          borderTopLeftRadius: "0.5rem",
                        },
                      },
                    },
                    "& .MuiTableBody-root": {
                      "& .MuiTableCell-root": {
                        textAlign: "right",
                        fontSize: "0.875rem",
                      },
                    },
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        dir="rtl"
      >
        <DialogTitle id="alert-dialog-title">
          تأكيد حذف المستخدم
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            هل أنت متأكد من حذف هذا المستخدم؟
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', p: 2 }}>
          <MDButton onClick={handleConfirmDelete} color="error" variant="contained" size="small">
            حذف
          </MDButton>
          <MDButton onClick={handleCloseDialog} color="secondary" variant="outlined" size="small">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Update User Dialog */}
      <Dialog
        open={updateDialog.open}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>تحديث بيانات المستخدم</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الاسم"
                name="displayName"
                value={updateForm.displayName}
                onChange={(e) => setUpdateForm({ ...updateForm, displayName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="البريد الإلكتروني"
                name="email"
                value={updateForm.email}
                onChange={(e) => setUpdateForm({ ...updateForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>نوع المستخدم</InputLabel>
                <Select
                  value={updateForm.userType}
                  label="نوع المستخدم"
                  onChange={(e) => setUpdateForm({ ...updateForm, userType: e.target.value })}
                  sx={{ minHeight: '56px' }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '300px'
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>اختر نوع المستخدم</em>
                  </MenuItem>
                  {Object.entries(userTypes).map(([name, value]) => (
                    <MenuItem key={value} value={value}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="company-label">الشركة</InputLabel>
                <Select
                  labelId="company-label"
                  id="companyId"
                  name="companyId"
                  value={updateForm.companyId}
                  label="الشركة"
                  onChange={(e) => setUpdateForm({ ...updateForm, companyId: e.target.value })}
                  sx={{
                    height: "45px",
                    "& .MuiSelect-select": {
                      paddingTop: "10px",
                      paddingBottom: "10px"
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>اختر الشركة</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
          <MDButton onClick={handleUpdateSubmit} variant="gradient" color="info" size="small">
            حفظ التغييرات
          </MDButton>
          <MDButton onClick={handleCloseUpdateDialog} variant="outlined" color="error" size="small">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={addDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>إضافة مستخدم جديد</DialogTitle>
        <DialogContent>
          <AddForm
            formData={addForm}
            handleChange={(e) => {
              const { name, value } = e.target;
              setAddForm((prev) => ({
                ...prev,
                [name]: value,
              }));
            }}
            handleSubmit={handleAddSubmit}
            validatePassword={validatePassword}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
          <MDButton onClick={handleAddSubmit} variant="gradient" color="info" size="small">
            إضافة
          </MDButton>
          <MDButton onClick={handleCloseAddDialog} variant="outlined" color="error" size="small">
            إلغاء
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Force Change Password Dialog */}
      <Dialog
        open={openForcePasswordDialog}
        onClose={() => {
          setOpenForcePasswordDialog(false);
          setNewPassword("");
          setConfirmPassword("");
          setPasswordErrors([]);
          setConfirmPasswordError("");
        }}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '16px',
            minWidth: '500px'
          }
        }}
      >
        {/* <MDBox
          mx={2}
          mt={2}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mb={2}
        >
          <MDTypography variant="h6" color="white">
            تغيير كلمة المرور
          </MDTypography>
        </MDBox> */}
        <DialogContent>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text" mb={2}>
              الرجاء إدخال كلمة المرور الجديدة للمستخدم
            </MDTypography>
            <MDBox mt={3}>
              <TextField
                autoFocus
                margin="dense"
                label="كلمة المرور الجديدة"
                type="password"
                fullWidth
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordErrors(validatePassword(e.target.value));
                  if (confirmPassword && e.target.value !== confirmPassword) {
                    setConfirmPasswordError("كلمات المرور غير متطابقة");
                  } else {
                    setConfirmPasswordError("");
                  }
                }}
                error={passwordErrors.length > 0}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  }
                }}
              />
            </MDBox>
            <MDBox mt={2}>
              <TextField
                margin="dense"
                label="تأكيد كلمة المرور"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (e.target.value !== newPassword) {
                    setConfirmPasswordError("كلمات المرور غير متطابقة");
                  } else {
                    setConfirmPasswordError("");
                  }
                }}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  }
                }}
              />
            </MDBox>
          </MDBox>
          {passwordErrors.length > 0 && (
            <MDBox
              mt={2}
              p={2}
              borderRadius="lg"
              bgcolor="#f8f9fa"
              border="1px solid #e9ecef"
            >
              <MDTypography variant="subtitle2" color="info" mb={1}>
                متطلبات كلمة المرور:
              </MDTypography>
              {passwordErrors.map((error, index) => (
                <MDTypography key={index} variant="caption" color="error" display="block">
                  • {error}
                </MDTypography>
              ))}
            </MDBox>
          )}
        </DialogContent>
        <MDBox display="flex" justifyContent="space-between" gap={2} px={3} pb={3}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleForceChangePassword}
            disabled={passwordErrors.length > 0 || !newPassword || !confirmPassword || !!confirmPasswordError}
          >
            تأكيد التغيير
          </MDButton>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => {
              setOpenForcePasswordDialog(false);
              setNewPassword("");
              setConfirmPassword("");
              setPasswordErrors([]);
              setConfirmPasswordError("");
            }}
          >
            إلغاء
          </MDButton>
        </MDBox>
      </Dialog>

      <MDSnackbar
        color={notification.type}
        icon={notification.type === "success" ? "check" : "warning"}
        title="لوحة ادارة ربان السفينة"
        content={notification.message}
        open={notification.show}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
        dir="rtl"
      />
      <Footer />
    </DashboardLayout>
  );
}

export default UsersDataTables;
